// Access to the files of the wasm build of SumatraPDF (sumatrapdf-ng) served under /sumatrapdf.
//
// sumatrapdf-ng keeps documents in the OPFS directory "uploads" (MEMFS /uploads,
// copied back after a save). Settings and thumbnails are in the OPFS directory
// "settings", which this page does not touch. See src/ng/gui/WasmShell.js.
//
// We are served from the same origin so we read and write that directory directly.
// A file added here can be opened in sumatrapdf-ng and the other way around.
//
// sumatrapdf-ng loads the directory into memory when it starts and writes back
// what it changed when it saves. It deletes only files it loaded or wrote, so a
// file added here while it's open in another tab survives its saves. Writes here
// take the same Web Lock as its saves (OPFS_LOCK), so the two don't interleave.

import { writeOpfsFile } from "./opfswrite";

export const NG_UPLOADS_DIR = "/uploads";

const NG_URL = "/sumatrapdf/";
const SIMPLE_VIEWER_URL = "/simpleviewer/";
const COMPARE_URL = "/compare/";
const UPLOADS_DIR = "uploads";
// the lock name WasmShell.js takes around its saves
const OPFS_LOCK = "sumatra-opfs";
let legacyUploadsOnce: Promise<void> | null = null;
// IndexedDB database emscripten's IDBFS used before OPFS. Imported once.
const LEGACY_DB = "/uploads";
const LEGACY_STORE = "FILE_DATA";
const LEGACY_MIGRATED_KEY = "sumatra-ng-uploads-opfs";

export interface NgFile {
  name: string;
  size: number;
  timestamp: Date;
}

interface LegacyEntry {
  timestamp?: Date;
  mode?: number;
  contents?: Uint8Array;
}

// url that opens a file from /uploads in sumatrapdf-ng
export function ngOpenURL(name: string): string {
  return `${NG_URL}?file=${encodeURIComponent(ngPath(name))}`;
}

// same ?file=/uploads/<name> argument as sumatrapdf-ng
export function simpleViewerURL(name: string): string {
  return `${SIMPLE_VIEWER_URL}?file=${encodeURIComponent(ngPath(name))}`;
}

// the page that shows a PDF page rendered by SumatraPDF, mupdf, pdfium and pdf.js
export function compareURL(name: string): string {
  return `${COMPARE_URL}?file=${encodeURIComponent(ngPath(name))}`;
}

export function isComparePath(pathname: string): boolean {
  return pathname === "/compare" || pathname === "/compare/";
}

export function isSimpleViewerPath(pathname: string): boolean {
  return pathname === "/simpleviewer" || pathname === "/simpleviewer/";
}

// file query is /uploads/<name>, the same value sumatrapdf-ng receives
export function fileNameFromFileArg(file: string): string | null {
  let name = file.trim();
  const prefix = NG_UPLOADS_DIR + "/";
  if (name.startsWith(prefix)) {
    name = name.slice(prefix.length);
  }
  if (!isValidNgFileName(name)) {
    return null;
  }
  return name;
}

export function isValidNgFileName(name: string): boolean {
  return name !== "" && name !== "." && name !== ".." && !/[\\/]/.test(name);
}

export async function listNgFiles(): Promise<NgFile[]> {
  const dir = await uploadsDir();
  if (!dir) {
    return [];
  }
  const files: NgFile[] = [];
  for await (const [name, handle] of dir.entries()) {
    if (handle.kind !== "file" || !isValidNgFileName(name)) {
      continue;
    }
    const file = await handle.getFile();
    files.push({ name, size: file.size, timestamp: new Date(file.lastModified) });
  }
  return files;
}

export async function readNgFile(name: string): Promise<Uint8Array> {
  const dir = await uploadsDir();
  if (!dir) {
    throw new Error("OPFS is not available");
  }
  try {
    const handle = await dir.getFileHandle(name);
    return new Uint8Array(await (await handle.getFile()).arrayBuffer());
  } catch {
    throw new Error(`file not found: ${name}`);
  }
}

// last modification time (ms) of a file, 0 if there's none
export async function ngFileModified(name: string): Promise<number> {
  const dir = await uploadsDir();
  try {
    return (await (await dir!.getFileHandle(name)).getFile()).lastModified;
  } catch {
    return 0;
  }
}

export function withOpfsLock<T>(fn: () => Promise<T>): Promise<T> {
  if (!navigator.locks) {
    return fn();
  }
  return navigator.locks.request(OPFS_LOCK, fn);
}

export function writeNgFile(name: string, data: Uint8Array): Promise<void> {
  return withOpfsLock(() => writeNgFileLocked(name, data));
}

export function deleteNgFile(name: string): Promise<void> {
  return withOpfsLock(() => deleteNgFileLocked(name));
}

export function renameNgFile(name: string, newName: string): Promise<void> {
  return withOpfsLock(() => renameNgFileLocked(name, newName));
}

async function writeNgFileLocked(name: string, data: Uint8Array): Promise<void> {
  if (!isValidNgFileName(name)) {
    throw new Error(`invalid file name: ${name}`);
  }
  const dir = await uploadsDir();
  if (!dir) {
    throw new Error("OPFS is not available");
  }
  const handle = await dir.getFileHandle(name, { create: true });
  await writeOpfsFile(handle, toArrayBuffer(data));
}

async function deleteNgFileLocked(name: string): Promise<void> {
  const dir = await uploadsDir();
  if (!dir) {
    return;
  }
  try {
    await dir.removeEntry(name);
  } catch (e) {
    if (e instanceof DOMException && e.name === "NotFoundError") {
      return;
    }
    throw e;
  }
}

async function renameNgFileLocked(name: string, newName: string): Promise<void> {
  if (!isValidNgFileName(newName)) {
    throw new Error(`invalid file name: ${newName}`);
  }
  if (name === newName) {
    return;
  }
  const dir = await uploadsDir();
  if (!dir) {
    throw new Error("OPFS is not available");
  }
  const handle = await dir.getFileHandle(name);
  if (await fileExists(dir, newName)) {
    throw new Error(`file already exists: ${newName}`);
  }
  const movable = handle as FileSystemFileHandle & { move?: (name: string) => Promise<void> };
  if (typeof movable.move === "function") {
    await movable.move(newName);
    return;
  }
  const data = new Uint8Array(await (await handle.getFile()).arrayBuffer());
  await writeNgFileLocked(newName, data);
  await deleteNgFileLocked(name);
}

function ngPath(name: string): string {
  return `${NG_UPLOADS_DIR}/${name}`;
}

async function uploadsDir(): Promise<FileSystemDirectoryHandle | null> {
  if (!navigator.storage?.getDirectory) {
    return null;
  }
  try {
    const root = await navigator.storage.getDirectory();
    const dir = await root.getDirectoryHandle(UPLOADS_DIR, { create: true });
    if (!(await dirHasEntries(dir))) {
      if (!legacyUploadsOnce) {
        legacyUploadsOnce = migrateLegacyUploads(dir).catch((e) => {
          legacyUploadsOnce = null;
          throw e;
        });
      }
      await legacyUploadsOnce;
    }
    return dir;
  } catch (e) {
    console.warn("opening OPFS uploads failed", e);
    return null;
  }
}

async function dirHasEntries(dir: FileSystemDirectoryHandle): Promise<boolean> {
  for await (const _ of dir.entries()) {
    return true;
  }
  return false;
}

async function fileExists(dir: FileSystemDirectoryHandle, name: string): Promise<boolean> {
  try {
    await dir.getFileHandle(name);
    return true;
  } catch {
    return false;
  }
}

// Copies files out of the old IDBFS database the first time OPFS is empty.
async function migrateLegacyUploads(dir: FileSystemDirectoryHandle): Promise<void> {
  if (localStorage.getItem(LEGACY_MIGRATED_KEY) === "1") {
    return;
  }
  const rows = await readLegacyUploads();
  for (const row of rows) {
    if (!row.contents || !isValidNgFileName(row.name)) {
      continue;
    }
    const handle = await dir.getFileHandle(row.name, { create: true });
    await writeOpfsFile(handle, toArrayBuffer(row.contents));
  }
  // Leave the IndexedDB database in place. A wasm build that still uses IDBFS
  // reads it, and the OPFS build deletes it after its own import. The flag
  // stops a later empty OPFS directory from copying those files back.
  localStorage.setItem(LEGACY_MIGRATED_KEY, "1");
}

function readLegacyUploads(): Promise<{ name: string; contents?: Uint8Array }[]> {
  return new Promise((resolve) => {
    let created = false;
    let settled = false;
    const finish = (rows: { name: string; contents?: Uint8Array }[]) => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(rows);
    };
    const req = indexedDB.open(LEGACY_DB);
    req.onupgradeneeded = () => {
      created = true;
      try {
        req.transaction?.abort();
      } catch {
        // aborting the creation transaction also errors the request
      }
    };
    req.onerror = () => finish([]);
    req.onsuccess = () => {
      const db = req.result;
      if (created || !db.objectStoreNames.contains(LEGACY_STORE)) {
        db.close();
        if (created) {
          indexedDB.deleteDatabase(LEGACY_DB);
        }
        finish([]);
        return;
      }
      const rows: { name: string; contents?: Uint8Array }[] = [];
      const tx = db.transaction(LEGACY_STORE, "readonly");
      const cur = tx.objectStore(LEGACY_STORE).openCursor();
      cur.onerror = () => {
        db.close();
        finish(rows);
      };
      cur.onsuccess = () => {
        const cursor = cur.result;
        if (!cursor) {
          return;
        }
        const entry = cursor.value as LegacyEntry | undefined;
        const name = legacyName(String(cursor.key));
        if (name && entry?.contents) {
          rows.push({ name, contents: entry.contents });
        }
        cursor.continue();
      };
      tx.oncomplete = () => {
        db.close();
        finish(rows);
      };
      tx.onerror = () => {
        db.close();
        finish(rows);
      };
    };
  });
}

function legacyName(path: string): string {
  const prefix = NG_UPLOADS_DIR + "/";
  if (!path.startsWith(prefix)) {
    return "";
  }
  const name = path.slice(prefix.length);
  return isValidNgFileName(name) ? name : "";
}

function toArrayBuffer(data: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(data.byteLength);
  copy.set(data);
  return copy.buffer;
}
